import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  getAllEngagements,
  getEngagementById,
  getAllLedgerEntrys,
  createLedgerEntry,
  createAuditLog,
  getAllBrands,
  getManufacturerProfile,
} from '../../store/actions';
import { GenCustomTable } from '../../components/customComponents/genCustomTable/GenCustomTable';
import { Header } from '../../components/layout/Header';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import Invoice, { downloadInvoicePDF } from '../../components/invoice/Invoice';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
  LEDGER_CATEGORY,
  LEDGER_STATUS,
  LEDGER_TYPE,
  PAYMENT_MODE,
  formatDate,
  formatINR,
  label,
  options,
  today,
} from '../../constants/garmentCodes';

import useCreateIntent, { CREATE_INTENT } from '../../customHooks/useCreateIntent';
const TYPE = { OPENING: 1, RECHARGE: 2, DEBIT: 3, REVERSAL: 6 };
const STATUS = { POSTED: 1, HISTORICAL: 5 };
const APPROVAL = { NA: 1, PENDING: 2 };
const inputCls = 'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400';

function useLoginUserId() {
  return useSelector((state) => state.auth?.loginUser?.userId ?? null);
}

function DebitModal({ open, onClose, engagement }) {
  const dispatch = useDispatch();
  const userId = useLoginUserId();
  const companyId = useSelector((state) => state.auth?.companyId);
  const [form, setForm] = useState({ amount: '', category: '', date: today(), description: '', reference: '' });
  const [showOverride, setShowOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const available = engagement?.availableBalance ?? 0;
  const amount = Number(form.amount) || 0;
  const insufficient = amount > available;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reset = () => {
    setForm({ amount: '', category: '', date: today(), description: '', reference: '' });
    setShowOverride(false);
    setOverrideReason('');
    setConfirmed(false);
  };

  const submit = () => {
    if (!engagement || !amount || !form.category || !form.description.trim()) return;
    if (insufficient && !showOverride) {
      setShowOverride(true);
      return;
    }
    if (insufficient && (!overrideReason.trim() || !confirmed)) return;
    dispatch(
      createLedgerEntry({
        payload: {
          engagementId: engagement.id,
          type: TYPE.DEBIT,
          category: Number(form.category),
          date: form.date,
          postingDate: today(),
          description: form.description.trim(),
          reference: form.reference || null,
          debit: amount,
          status: STATUS.POSTED,
          approvalStatus: insufficient ? APPROVAL.PENDING : APPROVAL.NA,
          postedByUserId: userId,
          hasAttachment: false,
        },
        callback: {
          success: (entry) => {
            dispatch(getEngagementById({ id: engagement.id }));
            if (insufficient) {
              dispatch(
                createAuditLog({
                  payload: {
                    companyId,
                    userId,
                    action: 'Debit Override Authorised',
                    entityType: 'LedgerEntry',
                    entityId: entry?.id,
                    engagementId: engagement.id,
                    summary: overrideReason.trim(),
                    beforeState: { availableBalance: available },
                    afterState: { availableBalance: entry?.availableBalance },
                  },
                }),
              );
            }
          },
        },
      }),
    );
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title="Create Debit" size="lg">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Engagement</label>
            <input disabled value={engagement?.name || ''} className={`${inputCls} bg-gray-50`} />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Category *</label>
            <select value={form.category} onChange={set('category')} className={inputCls}>
              <option value="">Select category</option>
              {options(LEDGER_CATEGORY).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Amount (INR) *</label>
            <input type="number" min="0" value={form.amount} onChange={set('amount')} className={`${inputCls} font-mono`} />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Debit Date *</label>
            <input type="date" value={form.date} onChange={set('date')} className={inputCls} />
          </div>
        </div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Description *</label>
          <input value={form.description} onChange={set('description')} placeholder="Describe the debit" className={inputCls} />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Linked Work Item</label>
          <input
            value={form.reference}
            onChange={set('reference')}
            placeholder="e.g. FAB-2026-007, DSN-2026-014, ORD-2026-032"
            className={`${inputCls} font-mono`}
          />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Supporting Document</label>
          <div className="border border-dashed border-gray-200 rounded-lg p-4 text-center">
            <p className="text-[12px] text-gray-400">Drop invoice or attachment here, or <span className="text-blue-600 cursor-pointer">browse</span></p>
          </div>
        </div>

        <div className={`rounded-lg p-4 text-sm ${insufficient ? 'bg-red-50 border border-red-200' : 'bg-gray-50 border border-gray-100'}`}>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <p className="text-[10px] text-gray-500 mb-0.5">Available Balance</p>
              <p className="font-semibold font-mono text-gray-800">{formatINR(available)}</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-500 mb-0.5">Debit Amount</p>
              <p className={`font-semibold font-mono ${insufficient ? 'text-red-600' : 'text-gray-800'}`}>{amount > 0 ? formatINR(amount) : '—'}</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-500 mb-0.5">Projected Balance</p>
              <p className={`font-semibold font-mono ${available - amount < 0 ? 'text-red-600' : 'text-gray-800'}`}>{amount > 0 ? formatINR(available - amount) : '—'}</p>
            </div>
          </div>
          {insufficient && (
            <p className="mt-3 text-[12px] text-red-700 font-medium">
              Insufficient available balance. This debit requires {formatINR(amount)}, but only {formatINR(available)} is available.
            </p>
          )}
        </div>

        {insufficient && showOverride && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
            <p className="text-[12px] font-semibold text-amber-800">⚠  Authorised Override</p>
            <p className="text-[12px] text-amber-700">The override is recorded in the audit log and is visible to the brand.</p>
            <textarea
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              rows={2}
              className="w-full text-sm border border-amber-200 rounded-lg px-3 py-2 focus:outline-none bg-white"
              placeholder="Business justification for this override…"
            />
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="rounded" />
              <span className="text-[12px] text-amber-800">I confirm this override and understand it will be audited</span>
            </label>
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <button onClick={onClose} className="flex-1 px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
          <button
            onClick={submit}
            disabled={!engagement || !amount || !form.category || !form.description.trim() || (showOverride && insufficient && (!overrideReason.trim() || !confirmed))}
            className={`flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 ${insufficient ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#1b3a6b] hover:bg-[#152d55]'}`}
          >
            {insufficient && !showOverride ? 'Insufficient — Override?' : insufficient ? 'Post with Override' : 'Post Debit'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RechargeModal({ open, onClose, engagement }) {
  const dispatch = useDispatch();
  const userId = useLoginUserId();
  const [form, setForm] = useState({ amount: '', date: today(), mode: '', reference: '' });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!engagement) return;
    const mode = label(PAYMENT_MODE, form.mode, '');
    dispatch(
      createLedgerEntry({
        payload: {
          engagementId: engagement.id,
          type: TYPE.RECHARGE,
          category: 2,
          date: form.date,
          postingDate: today(),
          description: ['Recharge', mode].filter(Boolean).join(' — '),
          reference: form.reference || null,
          credit: Number(form.amount),
          status: STATUS.POSTED,
          approvalStatus: APPROVAL.NA,
          postedByUserId: userId,
          hasAttachment: false,
        },
        callback: { success: () => dispatch(getEngagementById({ id: engagement.id })) },
      }),
    );
    setForm({ amount: '', date: today(), mode: '', reference: '' });
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title="Record Recharge" size="md">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Engagement</label>
            <input disabled value={engagement?.name || ''} className={`${inputCls} bg-gray-50`} />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Amount (INR) *</label>
            <input type="number" min="1" required value={form.amount} onChange={set('amount')} className={`${inputCls} font-mono`} />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Payment Date *</label>
            <input type="date" required value={form.date} onChange={set('date')} className={inputCls} />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Payment Mode *</label>
            <select required value={form.mode} onChange={set('mode')} className={inputCls}>
              <option value="">Select mode</option>
              {options(PAYMENT_MODE).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Transaction ID / UTR *</label>
            <input required value={form.reference} onChange={set('reference')} placeholder="UTR or transaction reference" className={`${inputCls} font-mono`} />
          </div>
        </div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={!engagement} className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55] disabled:opacity-50">Record Recharge</button>
        </div>
      </form>
    </Modal>
  );
}

export default function Wallets() {
  const dispatch = useDispatch();
  const [params, setParams] = useSearchParams();
  const userId = useLoginUserId();
  const [debitOpen, setDebitOpen] = useState(false);
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [viewEntry, setViewEntry] = useState(null);

  const engagements = useSelector((state) => state.engagement.engagements);
  const engagement = useSelector((state) => state.engagement.engagement);
  const entries = useSelector((state) => state.ledgerentry.entries);
  const loading = useSelector((state) => state.ledgerentry.loading);
  const manufacturer = useSelector((state) => state.manufacturer?.profile);
  const brands = useSelector((state) => state.brand?.brands);

  const brandId = params.get('brandId') || '';
  const engagementId = params.get('engagementId') || '';
  const currentBrand = brands?.find((b) => b.id === engagement?.brandId) || null;

  useCreateIntent(CREATE_INTENT.DEBIT, () => setDebitOpen(true), !!engagement);

  useEffect(() => {
    dispatch(getAllBrands());
    dispatch(getManufacturerProfile());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      getAllEngagements({
        brandId,
        callback: {
          success: (list) => {
            if (!engagementId && list.length) setParams({ ...(brandId ? { brandId } : {}), engagementId: String(list[0].id) }, { replace: true });
          },
        },
      }),
    );
  }, [dispatch, brandId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (engagementId) dispatch(getEngagementById({ id: engagementId }));
  }, [dispatch, engagementId]);

  useEffect(() => {
    dispatch(getAllLedgerEntrys({ engagementId, type: '' }));
  }, [dispatch, engagementId]);

  const threshold = engagement?.lowBalanceThreshold;
  const low = threshold != null && (engagement?.availableBalance ?? 0) < threshold;
  const totalCredits = entries.reduce((sum, e) => sum + (Number(e.credit) || 0), 0);
  const totalDebits = entries.reduce((sum, e) => sum + (Number(e.debit) || 0), 0);

  const reverse = (entry) => {
    const isCredit = Number(entry.credit) > 0;
    dispatch(
      createLedgerEntry({
        payload: {
          engagementId: entry.engagementId,
          type: TYPE.REVERSAL,
          category: 9,
          date: today(),
          postingDate: today(),
          description: `Reversal — ${entry.description || entry.reference || `entry #${entry.id}`}`,
          reference: entry.reference ? `REV-${entry.reference}` : null,
          credit: isCredit ? null : Number(entry.debit),
          debit: isCredit ? Number(entry.credit) : null,
          status: STATUS.POSTED,
          approvalStatus: APPROVAL.NA,
          postedByUserId: userId,
          linkedItemType: 'LedgerEntry',
          linkedItemId: entry.id,
        },
        callback: { success: () => dispatch(getEngagementById({ id: entry.engagementId })) },
      }),
    );
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-[#f8f9fa]">
      <Header title="Wallets & Ledger" breadcrumbs={[{ workspace: true }, { label: 'Wallets & Ledger' }]} />

      <main className="flex-1 overflow-auto p-6 pb-20 md:pb-6">
        <div className="flex items-center gap-3 mb-6">
          <label className="text-[12px] font-medium text-gray-500">Engagement:</label>
          <select
            value={engagementId}
            onChange={(e) => setParams({ ...(brandId ? { brandId } : {}), engagementId: e.target.value })}
            className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:border-blue-400 bg-white"
          >
            {!engagements.length && <option value="">No engagements</option>}
            {engagements.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <div className="flex gap-2 ml-auto">
            <button disabled={!engagement} onClick={() => setRechargeOpen(true)} className="px-3 py-1.5 text-[12px] font-medium text-[#1b3a6b] border border-[#1b3a6b]/30 rounded-lg hover:bg-blue-50 disabled:opacity-50">
              + Record Recharge
            </button>
            <button disabled={!engagement} onClick={() => setDebitOpen(true)} className="px-3 py-1.5 text-[12px] font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55] disabled:opacity-50">
              + Add Debit
            </button>
          </div>
        </div>

        {low && (
          <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-[12px] text-amber-800">
            <span className="font-bold">⚠ </span>
            <span>Available balance is below the {formatINR(threshold)} threshold. New chargeable work is blocked until recharged.</span>
          </div>
        )}

        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-100 p-5">
            <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-2">Current Balance</p>
            <p className="text-[28px] font-bold font-mono text-gray-900">{formatINR(engagement?.walletBalance)}</p>
            <p className="text-[11px] text-gray-400 mt-1">Total credits minus total debits</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-100 p-5">
            <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-2">Held / Committed</p>
            <p className="text-[28px] font-bold font-mono text-amber-600">{formatINR(engagement?.heldAmount)}</p>
            <p className="text-[11px] text-gray-400 mt-1">Approved, not yet billed</p>
          </div>
          <div className={`rounded-lg border p-5 ${low ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-100'}`}>
            <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-2">Available Balance</p>
            <p className={`text-[28px] font-bold font-mono ${low ? 'text-amber-600' : 'text-green-600'}`}>{formatINR(engagement?.availableBalance)}</p>
            <p className="text-[11px] text-gray-400 mt-1">Gates new chargeable work</p>
            {low && <span className="inline-block mt-2 text-[10px] bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full font-medium">Below threshold</span>}
          </div>
        </div>

        <div className="grid grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total Credits', val: formatINR(totalCredits), color: 'text-green-600' },
            { label: 'Total Debits', val: formatINR(totalDebits), color: 'text-red-600' },
            { label: 'Last Activity', val: formatDate(engagement?.lastActivity), color: 'text-gray-700' },
            { label: 'Low-Balance Threshold', val: threshold != null ? formatINR(threshold) : '—', color: 'text-gray-700' },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-lg border border-gray-100 px-4 py-3">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">{s.label}</p>
              <p className={`text-[14px] font-semibold font-mono ${s.color}`}>{s.val}</p>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3 mb-4">
          <h3 className="text-[13px] font-semibold text-gray-800">Ledger Entries</h3>
          {loading && <span className="text-[11px] text-gray-400">Loading…</span>}
        </div>

        <div className="bg-white rounded-lg border border-gray-100 overflow-hidden [&_.MuiTablePagination-toolbar]:!justify-end [&_.MuiTablePagination-spacer]:!flex-1 [&_.MuiTablePagination-spacer]:!block [&_.MuiTablePagination-selectLabel]:!m-0 [&_.MuiTablePagination-displayedRows]:!m-0 [&_.MuiTablePagination-root]:!text-[#5C5A54]">
          <GenCustomTable
            isExportEnabled={true}
            exportFileName="Wallet Ledger"
            columns={[
              { id: 'date', label: 'DATE', align: 'left' },
              { id: 'type', label: 'TYPE', align: 'left' },
              { id: 'category', label: 'CATEGORY', align: 'left' },
              { id: 'description', label: 'DESCRIPTION', align: 'left' },
              { id: 'reference', label: 'REFERENCE', align: 'left' },
              { id: 'credit', label: 'CREDIT', align: 'right' },
              { id: 'debit', label: 'DEBIT', align: 'right' },
              { id: 'held', label: 'HELD', align: 'right' },
              { id: 'runningBalance', label: 'RUNNING BALANCE', align: 'right' },
              { id: 'available', label: 'AVAILABLE', align: 'right' },
              { id: 'status', label: 'STATUS', align: 'left' },
              { id: 'actions', label: 'ACTIONS', align: 'left', disableFilter: true },
            ]}
            rows={entries.map((entry) => {
              const type = label(LEDGER_TYPE, entry.type);
              const status = label(LEDGER_STATUS, entry.status);
              const historical = entry.status === STATUS.HISTORICAL;
              const reversible = (entry.type === TYPE.DEBIT || entry.type === TYPE.RECHARGE) && !historical && !entry.__optimistic;
              return {
                id: entry.id,
                rColumns: [
                  <div key="date" label={entry.date || ''} className={`flex items-center w-full h-full px-2 py-1 font-mono text-[12px] text-gray-600 whitespace-nowrap ${entry.__optimistic ? 'opacity-60' : ''}`}>
                    {formatDate(entry.date)}
                    {historical && <span className="ml-1 text-[9px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">HIST</span>}
                  </div>,
                  <div key="type" label={type} className="flex items-center w-full h-full px-2 py-1"><Badge status={type} /></div>,
                  <div key="cat" label={label(LEDGER_CATEGORY, entry.category)} className="flex items-center w-full h-full px-2 py-1 text-[12px] text-gray-600">{label(LEDGER_CATEGORY, entry.category)}</div>,
                  <div key="desc" label={entry.description || ''} className="flex items-center w-full h-full px-2 py-1 text-[12px] text-gray-700 max-w-xs truncate">{entry.description || '—'}</div>,
                  <div key="ref" label={entry.reference || ''} className="flex items-center w-full h-full px-2 py-1 font-mono text-[11px] text-gray-500 whitespace-nowrap">{entry.reference || '—'}</div>,
                  <div key="credit" label={entry.credit ? formatINR(entry.credit) : ''} className="flex items-center justify-end w-full h-full px-2 py-1 font-mono text-[12px] text-green-600 whitespace-nowrap">{entry.credit ? formatINR(entry.credit) : ''}</div>,
                  <div key="debit" label={entry.debit ? formatINR(entry.debit) : ''} className="flex items-center justify-end w-full h-full px-2 py-1 font-mono text-[12px] text-red-600 whitespace-nowrap">{entry.debit ? formatINR(entry.debit) : ''}</div>,
                  <div key="held" label={entry.held ? formatINR(entry.held) : ''} className="flex items-center justify-end w-full h-full px-2 py-1 font-mono text-[12px] text-amber-600 whitespace-nowrap">{entry.held ? formatINR(entry.held) : ''}</div>,
                  <div key="run" label={formatINR(entry.runningBalance)} className="flex items-center justify-end w-full h-full px-2 py-1 font-mono text-[12px] text-gray-800 whitespace-nowrap font-semibold">{entry.runningBalance != null ? formatINR(entry.runningBalance) : '…'}</div>,
                  <div key="avail" label={formatINR(entry.availableBalance)} className="flex items-center justify-end w-full h-full px-2 py-1 font-mono text-[12px] text-gray-600 whitespace-nowrap">{entry.availableBalance != null ? formatINR(entry.availableBalance) : '…'}</div>,
                  <div key="stat" label={status} className="flex items-center w-full h-full px-2 py-1"><Badge status={status} /></div>,
                  <div key="act" label="" className="flex items-center w-full h-full px-2 py-1 gap-2">
                    <button
                      onClick={() => setViewEntry(entry)}
                      className="text-[12px] text-blue-600 font-medium hover:underline whitespace-nowrap cursor-pointer"
                    >
                      View
                    </button>
                    <button
                      onClick={() => downloadInvoicePDF({ entry, engagement, manufacturer, brand: currentBrand })}
                      className="text-gray-500 hover:text-[#1b3a6b] p-0.5 rounded hover:bg-gray-100 transition-colors flex items-center justify-center cursor-pointer"
                      title="Download Invoice PDF"
                    >
                      <FileDownloadOutlinedIcon sx={{ fontSize: 17 }} />
                    </button>
                    {reversible && (
                      <button
                        onClick={() => reverse(entry)}
                        className="text-[11px] text-gray-400 hover:text-gray-700 whitespace-nowrap cursor-pointer"
                      >
                        Reverse
                      </button>
                    )}
                  </div>,
                ],
              };
            })}
          />
          {!loading && entries.length === 0 && <p className="py-10 text-center text-[12px] text-gray-400">No ledger entries for this engagement</p>}
        </div>
      </main>

      <DebitModal open={debitOpen} onClose={() => setDebitOpen(false)} engagement={engagement} />
      <RechargeModal open={rechargeOpen} onClose={() => setRechargeOpen(false)} engagement={engagement} />
      <Invoice
        open={!!viewEntry}
        onClose={() => setViewEntry(null)}
        entry={viewEntry}
        engagement={engagement}
        manufacturer={manufacturer}
        brand={currentBrand}
      />
    </div>
  );
}
